var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/naming-convention */
/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import { createContext, forwardRef, useCallback, useContext, } from 'react';
import { classNames } from '../_common/defaultImports';
import { IconButton } from '../button';
import { Divider } from '../divider';
import { CollapseExpandLeftIcon, CollapseExpandRightIcon } from '../icons';
const getExpandIconName = (position, expanded) => {
    if (expanded) {
        return position === 'left' ? (_jsx(CollapseExpandLeftIcon, { className: "n-size-full" })) : (_jsx(CollapseExpandRightIcon, { className: "n-size-full" }));
    }
    else {
        return position === 'left' ? (_jsx(CollapseExpandRightIcon, { className: "n-size-full" })) : (_jsx(CollapseExpandLeftIcon, { className: "n-size-full" }));
    }
};
const SideNavigationComponent = forwardRef(function SideNavigationComponent(_a, ref) {
    var { expanded, children, className, onExpandedChange, iconMenu = false, position = 'left', size: defaultSize = 'large' } = _a, restProps = __rest(_a, ["expanded", "children", "className", "onExpandedChange", "iconMenu", "position", "size"]);
    // Typescript unable to infer the default value correctly in the parameter.
    const size = defaultSize;
    const setOnExpandedChange = useCallback(() => {
        if (onExpandedChange !== undefined) {
            onExpandedChange(!expanded);
        }
    }, [expanded, onExpandedChange]);
    const classes = classNames('ndl-side-navigation', className, {
        'ndl-expanded': expanded,
        'ndl-side-navigation-minimized-small': !expanded && size === 'small',
        [`ndl-${position}`]: position,
    });
    const icon = getExpandIconName(position, expanded);
    return (_jsx(SideNavigationContext.Provider, { value: { expanded, iconMenu, size }, children: _jsxs("div", Object.assign({}, restProps, { className: classes, ref: ref }, (!expanded &&
            !iconMenu && {
            'aria-label': 'open-side-navigation',
            onClick: setOnExpandedChange,
        }), { children: [_jsx("nav", { className: "ndl-side-navigation-nav", children: children }), onExpandedChange !== undefined && (_jsx(IconButton, { className: "ndl-side-navigation-drawer-button", onClick: setOnExpandedChange, "aria-label": expanded ? 'collapse-navigation' : 'expand-navigation', size: size === 'small' ? 'medium' : 'large', "data-testid": "ndl-side-nav-expand-btn", title: `${expanded ? 'Collapse' : 'Expand'} side navigation`, clean: true, children: icon }))] })) }));
});
SideNavigationComponent.displayName = 'SideNavigation';
const SideNavigationContext = createContext(null);
const useSideNavigationContext = () => useContext(SideNavigationContext);
const SideNavigationListContext = createContext(null);
const useSideNavigationListContext = () => useContext(SideNavigationListContext);
const SideNavigationList = forwardRef(function SideNavigationList(_a, ref) {
    var _b, _c;
    var { children, className } = _a, restProps = __rest(_a, ["children", "className"]);
    const classes = classNames('ndl-side-navigation-nav-list', className);
    const { level } = (_b = useSideNavigationListContext()) !== null && _b !== void 0 ? _b : {
        level: -1,
    };
    const { expanded, iconMenu, size } = (_c = useSideNavigationContext()) !== null && _c !== void 0 ? _c : {
        expanded: true,
        iconMenu: false,
        size: 'small',
    };
    if (!expanded && !iconMenu)
        return null;
    return (_jsx(SideNavigationListContext.Provider, { value: { level: level + 1, size }, children: _jsx("ul", Object.assign({ className: classes, ref: ref }, restProps, { children: children })) }));
});
SideNavigationList.displayName = 'SideNavigation.List';
const SideNavigationItem = forwardRef(function SideNavigationItem(_a, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
ref) {
    var _b, _c;
    var { className, children, icon, as, selected = false, selectable = true, style = {} } = _a, restProps = __rest(_a, ["className", "children", "icon", "as", "selected", "selectable", "style"]);
    const Component = as || 'a';
    const { level } = (_b = useSideNavigationListContext()) !== null && _b !== void 0 ? _b : { level: 0 };
    const { expanded, iconMenu, size } = (_c = useSideNavigationContext()) !== null && _c !== void 0 ? _c : {
        expanded: true,
        iconMenu: false,
        size: 'small',
    };
    const defaultPaddingLeft = level ? 0 : size === 'small' ? 8 : 12;
    const paddingLeft = expanded ? defaultPaddingLeft + level * 32 : 0;
    const itemClasses = classNames(className, {
        'ndl-side-navigation-nav-item': size === 'large',
        'ndl-side-navigation-nav-item-small': size === 'small',
        'ndl-expanded': expanded,
    });
    const innerClasses = classNames('ndl-side-navigation-inner-item', {
        'ndl-selected': selected,
        'ndl-selectable': selectable,
    });
    if (iconMenu && !icon) {
        throw new Error('SideNavigation is an IconMenu but icon is not defined! Set an icon on this SideNavigationItem or set `iconMenu` to false on SideNavigation');
    }
    else if (!iconMenu && icon) {
        throw new Error('SideNavigation is not an IconMenu but icon is defined! Remove the icon or set `iconMenu` to true on SideNavigation to allow icons');
    }
    if (!expanded && !(icon && iconMenu))
        return null;
    return (_jsx("li", { className: itemClasses, children: _jsxs(Component, Object.assign({ className: innerClasses, style: Object.assign({ paddingLeft }, style), ref: ref }, restProps, { children: [iconMenu && icon && _jsx("span", { className: "ndl-icon", children: icon }), expanded && children] })) }));
});
SideNavigationItem.displayName = 'SideNavigation.Item';
const SideNavigationGroupHeader = forwardRef(function SideNavigationGroupHeader(_a, ref) {
    var _b;
    var { children, className, as, style } = _a, restProps = __rest(_a, ["children", "className", "as", "style"]);
    const Component = as !== null && as !== void 0 ? as : 'div';
    const { expanded, iconMenu, size } = (_b = useSideNavigationContext()) !== null && _b !== void 0 ? _b : {
        expanded: true,
        iconMenu: false,
    };
    const classes = classNames(className, {
        'ndl-side-navigation-nav-item': size === 'large',
        'ndl-side-navigation-nav-item-small': size === 'small',
    });
    const paddingLeft = expanded ? (size === 'small' ? '12px' : '16px') : '0';
    return (_jsx("li", Object.assign({ className: classes, role: "presentation", style: Object.assign({ paddingLeft }, style) }, restProps, { children: _jsxs(Component, { className: "ndl-side-navigation-inner-item ndl-side-navigation-group-header", ref: ref, children: [!expanded && iconMenu && _jsx(Divider, {}), expanded && children] }) })));
});
SideNavigationGroupHeader.displayName = 'SideNavigation.GroupHeader';
// Issue with TypeScript forwardRef and subcomponents: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/34757#issuecomment-894053907
const SideNavigation = Object.assign(SideNavigationComponent, {
    List: SideNavigationList,
    GroupHeader: SideNavigationGroupHeader,
    Item: SideNavigationItem,
});
export { SideNavigation };
//# sourceMappingURL=SideNavigation.js.map